// ============================================================
// G3：漫游/巡检录制（WebGL canvas captureStream + MediaRecorder → webm）
// 用法：startSceneRecording() → 用户播放漫游/路径 → stopSceneRecording() 得到 Blob
// ============================================================
import { sceneImageExportRegistry } from '../stores/sceneImageExportStore';

let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let mimeType = 'video/webm';

export function isSceneRecordingSupported(): boolean {
  const canvas = sceneImageExportRegistry.getCanvas?.() as (HTMLCanvasElement & { captureStream?: (fps?: number) => MediaStream }) | null;
  return !!canvas && typeof canvas.captureStream === 'function' && typeof MediaRecorder !== 'undefined';
}

export function isSceneRecording(): boolean {
  return !!recorder && recorder.state === 'recording';
}

/** 开始录制（默认 30fps / 8Mbps）；不支持时返回 false */
export function startSceneRecording(opts: { fps?: number; bitsPerSecond?: number } = {}): boolean {
  if (isSceneRecording()) return true;
  const canvas = sceneImageExportRegistry.getCanvas?.() as (HTMLCanvasElement & { captureStream?: (fps?: number) => MediaStream }) | null;
  if (!canvas || typeof canvas.captureStream !== 'function' || typeof MediaRecorder === 'undefined') return false;
  try {
    const stream = canvas.captureStream(opts.fps ?? 30);
    mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
    recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: opts.bitsPerSecond ?? 8_000_000 });
    chunks = [];
    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    recorder.start(1000);
    return true;
  } catch {
    recorder = null;
    return false;
  }
}

/** 停止录制并返回 webm Blob（未录制返回 null） */
export function stopSceneRecording(): Promise<Blob | null> {
  return new Promise((resolve) => {
    const rec = recorder;
    if (!rec) {
      resolve(null);
      return;
    }
    rec.onstop = () => {
      const blob = chunks.length > 0 ? new Blob(chunks, { type: mimeType }) : null;
      recorder = null;
      chunks = [];
      resolve(blob);
    };
    try {
      rec.stop();
    } catch {
      recorder = null;
      chunks = [];
      resolve(null);
    }
  });
}

export function downloadSceneRecording(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
